import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  LocalPizza, 
  TouchApp, 
  BakeryDining, 
  DeliveryDining, 
  Assessment 
} from '@mui/icons-material';
import PublicLayout from "../../layouts/PublicLayout";

export default function Pizzeria() {
  return (
    <PublicLayout>
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: '#f6521a', fontWeight: 800, letterSpacing: 1.5 }}>
                TPOS FOR PIZZERIAS
              </Typography>
              
              <Typography variant="h2" sx={{ fontWeight: 600, mt: 1, mb: 2, fontSize: { xs: '1.5rem', md: '2.5rem' }, lineHeight: 1.1 }}>
                From Crust to Toppings, Manage Every Slice with <span style={{ color: "#f6521a" }}>Precision.</span>
              </Typography>

              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.5 }}>
                Master the art of complex pizza modifications, half-and-half logic, and lightning-fast home delivery management.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.primary', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                A pizzeria is one of the most complex food businesses to manage digitally. With infinite combinations of crusts, sizes, sauces, and toppings—plus the challenge of half-and-half orders—your POS needs to be more than just a calculator. 
                <strong> TPOS provides an intelligent pizza-builder interface</strong> that ensures 100% order accuracy, while our backend monitors your dough fermentation cycles and expensive topping inventory (like pepperoni and cheese) down to the gram.
              </Typography>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Specialized TPOS Features:
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    title: "Half-and-Half Logic Engine",
                    desc: "Easily split a pizza into two different flavor profiles with automated pricing based on the higher-value half or a custom average.",
                    icon: <LocalPizza sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Visual Pizza Builder", 
                    desc: "A drag-and-drop style interface for cashiers to select crusts, sizes, and toppings without leaving the main screen.",
                    icon: <TouchApp sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Dough & Batch Tracking", 
                    desc: "Manage your dough preparation cycles. Track how many dough balls are prepped, proofed, and wasted daily.",
                    icon: <BakeryDining sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Integrated Delivery Dispatcher", 
                    desc: "Assign orders to your own riders. Track 'Time in Oven' vs. 'Time on Road' to optimize your 30-minute delivery promise.",
                    icon: <DeliveryDining sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Inventory Yield Monitoring", 
                    desc: "Compare your 'Theoretical vs. Actual' cheese usage to ensure exact standardized portion control.",
                    icon: <Assessment sx={{ color: '#f6521a' }} />
                  }
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={5} sx={{ position: 'sticky', top: 100 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden', 
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 550 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'right',
                    float:'right'                    
                  }}
                >
                  <img 
                    src="/img/home-banner.jpg" 
                    alt="Pizzeria POS System" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                
                <Box sx={{ p: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Business Impact</Typography>
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Increase order accuracy to 100%, eliminating costly re-fires due to misunderstood topping requests." />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Reduce topping wastage by 15-20% through strict yield auditing and portion control." />
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </PublicLayout>
  );
}